import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, CategorySelect, Input, KeywordsInput, LocationPicker, UbigeoSelect } from "@vimos/ui";
import {
  getDirectoryCategoryLabel,
  normalizeSocialProfileUrl,
  type DirectorySocialNetwork,
} from "@vimos/api-client";
import { canEdit, useAuth } from "../../lib/auth";
import {
  publicSiteUrl,
  fetchDirectoryStore,
  uploadDirectoryImage,
  updateDirectorySocialLinks,
  updateDirectoryStore,
} from "./directoryApi";
import {
  buildCategoryKeywordsPatch,
  buildManualContentPatch,
  describeStoreState,
  saasManagementUrl,
  storeToEditForm,
  validateStoreEditForm,
  type DirectoryAdminPatch,
  type EditorialStatus,
  type StoreEditForm,
} from "./directoryLib";
import { ConfirmDialog } from "./ConfirmDialog";
import { ToastStack, useToasts } from "./Toast";

const saasAdminBaseUrl = import.meta.env.VITE_SAAS_ADMIN_BASE_URL as string | undefined;

const selectClass =
  "border-sand/60 bg-paper text-ink h-11 rounded-md border px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:bg-sand/20 disabled:opacity-70";
const textareaClass =
  "border-sand/70 bg-paper text-ink focus-visible:ring-gold min-h-28 w-full rounded-md border px-3.5 py-2 text-base focus-visible:outline-none focus-visible:ring-2 disabled:bg-sand/20 disabled:opacity-70";

const emptyForm: StoreEditForm = {
  name: "",
  category: "",
  city: "",
  department: "",
  province: "",
  district: "",
  address: "",
  phone: "",
  whatsapp: "",
  website_url: "",
  description: "",
  location: null,
};

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
      <div className="border-sand/40 border-b pb-3">
        <h2 className="font-display text-ink text-lg font-bold">{title}</h2>
        {description ? <p className="text-mist mt-1 text-sm">{description}</p> : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-body text-ink text-sm font-medium">{label}</span>
      <p className="border-sand/50 bg-sand/10 text-ink rounded-md border px-3.5 py-2.5 text-sm">
        {value || <span className="text-mist">Sin dato</span>}
      </p>
    </div>
  );
}

export function StoreEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userCanEdit = canEdit(profile);
  const { toasts, pushSuccess, pushError, dismiss } = useToasts();

  const {
    data: store,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["stores_directory", "detail", id],
    queryFn: () => fetchDirectoryStore(id!),
    enabled: Boolean(id),
  });

  const [form, setForm] = useState<StoreEditForm>(emptyForm);
  const [baseline, setBaseline] = useState<StoreEditForm>(emptyForm);
  const [categoryId, setCategoryId] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [socialLinks, setSocialLinks] = useState<Record<DirectorySocialNetwork, string>>({
    tiktok: "",
    instagram: "",
    facebook: "",
  });
  const [baselineSocialLinks, setBaselineSocialLinks] = useState<Record<string, string>>({});
  const [baselineClassification, setBaselineClassification] = useState<{
    category_id: string | null;
    keywords: string[];
  }>({ category_id: null, keywords: [] });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [baselineImages, setBaselineImages] = useState({
    logo: null as string | null,
    cover: null as string | null,
    gallery: [] as string[],
  });
  const [editorialStatus, setEditorialStatus] = useState<EditorialStatus>("visible");
  const [isVisible, setIsVisible] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [isForceHidden, setIsForceHidden] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);

  const isManual = store?.origin === "manual";

  useEffect(() => {
    if (!store) return;
    const nextForm = storeToEditForm(store);
    setForm(nextForm);
    setBaseline(nextForm);
    setCategoryId(store.category_id ?? "");
    setKeywords(Array.isArray(store.keywords) ? store.keywords : []);
    const socials = store.social_links ?? {};
    setSocialLinks({
      tiktok: socials.tiktok ?? "",
      instagram: socials.instagram ?? "",
      facebook: socials.facebook ?? "",
    });
    setBaselineSocialLinks(socials);
    setBaselineClassification({
      category_id: store.category_id ?? null,
      keywords: Array.isArray(store.keywords) ? store.keywords : [],
    });
    setLogoUrl(store.logo_url);
    setCoverUrl(store.cover_image_url);
    setGallery(Array.isArray(store.gallery) ? store.gallery : []);
    setBaselineImages({
      logo: store.logo_url,
      cover: store.cover_image_url,
      gallery: Array.isArray(store.gallery) ? store.gallery : [],
    });
    setEditorialStatus(store.editorial_status);
    setIsVisible(store.is_visible_in_directory);
    setIsFeatured(store.is_featured);
    setSortOrder(store.sort_order);
    setIsForceHidden(store.is_force_hidden);
    setFieldErrors({});
    setSaveError(null);
  }, [store]);

  const dirty = useMemo(() => {
    if (!store) return false;
    const contentChanged = isManual && Object.keys(buildManualContentPatch(baseline, form)).length > 0;
    const socialChanged =
      isManual &&
      JSON.stringify(socialLinks) !==
        JSON.stringify({
          tiktok: baselineSocialLinks.tiktok ?? "",
          instagram: baselineSocialLinks.instagram ?? "",
          facebook: baselineSocialLinks.facebook ?? "",
        });
    const imagesChanged =
      isManual &&
      (logoUrl !== baselineImages.logo ||
        coverUrl !== baselineImages.cover ||
        JSON.stringify(gallery) !== JSON.stringify(baselineImages.gallery));
    const adminChanged =
      editorialStatus !== store.editorial_status ||
      isFeatured !== store.is_featured ||
      sortOrder !== store.sort_order ||
      isForceHidden !== store.is_force_hidden ||
      (isManual && isVisible !== store.is_visible_in_directory);
    const classificationChanged =
      Object.keys(
        buildCategoryKeywordsPatch(baselineClassification, {
          category_id: categoryId || null,
          keywords,
        }),
      ).length > 0;
    return contentChanged || socialChanged || imagesChanged || adminChanged || classificationChanged;
  }, [
    store,
    isManual,
    baseline,
    form,
    logoUrl,
    coverUrl,
    gallery,
    baselineImages,
    baselineClassification,
    categoryId,
    keywords,
    socialLinks,
    baselineSocialLinks,
    editorialStatus,
    isFeatured,
    sortOrder,
    isForceHidden,
    isVisible,
  ]);

  useEffect(() => {
    if (!dirty) return;
    function handler(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const save = useMutation({
    mutationFn: async () => {
      if (!store) throw new Error("El negocio no esta cargado.");
      const patch: DirectoryAdminPatch = isManual ? buildManualContentPatch(baseline, form) : {};
      const socialChanged =
        JSON.stringify(socialLinks) !==
        JSON.stringify({
          tiktok: baselineSocialLinks.tiktok ?? "",
          instagram: baselineSocialLinks.instagram ?? "",
          facebook: baselineSocialLinks.facebook ?? "",
        });
      let socialLinksForSaas: Record<string, string> | null = null;

      if (socialChanged) {
        const normalizedSocials: Record<string, string> = {};
        for (const network of ["tiktok", "instagram", "facebook"] as const) {
          const value = socialLinks[network].trim();
          if (!value) continue;
          const normalized = normalizeSocialProfileUrl(network, value);
          if (!normalized)
            throw new Error(`El enlace de ${network} debe ser un perfil válido de esa red social.`);
          normalizedSocials[network] = normalized;
        }
        if (isManual) patch.social_links = normalizedSocials;
        else socialLinksForSaas = normalizedSocials;
      }

      if (isManual) {
        if (logoUrl !== baselineImages.logo) patch.logo_url = logoUrl;
        if (coverUrl !== baselineImages.cover) patch.cover_image_url = coverUrl;
        if (JSON.stringify(gallery) !== JSON.stringify(baselineImages.gallery)) patch.gallery = gallery;
      }

      Object.assign(
        patch,
        buildCategoryKeywordsPatch(baselineClassification, {
          category_id: categoryId || null,
          keywords,
        }),
      );
      // En negocios manuales, la columna `category` (texto publico) se alinea
      // con la etiqueta de la categoria controlada elegida.
      if (isManual && categoryId && categoryId !== (baselineClassification.category_id ?? "")) {
        patch.category = getDirectoryCategoryLabel(categoryId);
      }

      if (editorialStatus !== store.editorial_status) patch.editorial_status = editorialStatus;
      if (isFeatured !== store.is_featured) patch.is_featured = isFeatured;
      if (sortOrder !== store.sort_order) patch.sort_order = sortOrder;
      if (isForceHidden !== store.is_force_hidden) patch.is_force_hidden = isForceHidden;
      if (isManual && isVisible !== store.is_visible_in_directory) {
        patch.is_visible_in_directory = isVisible;
      }

      if (Object.keys(patch).length === 0 && !socialLinksForSaas) {
        throw new Error("No hay cambios para guardar.");
      }
      if (Object.keys(patch).length > 0) await updateDirectoryStore(store.id, patch);
      if (socialLinksForSaas) await updateDirectorySocialLinks(store.id, socialLinksForSaas);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
      queryClient.invalidateQueries({ queryKey: ["stores_directory", "detail", id] });
      setSaveError(null);
      pushSuccess("Cambios guardados.");
    },
    onError: (mutationError) => {
      const message = mutationError instanceof Error ? mutationError.message : "No se pudo guardar.";
      setSaveError(message);
      pushError(message);
    },
  });

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!store || !userCanEdit) return;
    if (isManual) {
      const errors = validateStoreEditForm(form);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        setSaveError("Revisa los campos marcados antes de guardar.");
        return;
      }
      for (const network of ["tiktok", "instagram", "facebook"] as const) {
        if (socialLinks[network].trim() && !normalizeSocialProfileUrl(network, socialLinks[network])) {
          setSaveError(`El enlace de ${network} debe ser un perfil válido de esa red social.`);
          return;
        }
      }
    }
    save.mutate();
  }

  function handleCancel() {
    if (dirty) {
      setConfirmCancel(true);
      return;
    }
    navigate("/directorio");
  }

  async function handleImage(
    event: React.ChangeEvent<HTMLInputElement>,
    target: "logo" | "cover" | "gallery",
  ) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    setImageBusy(true);
    setSaveError(null);
    try {
      if (target === "logo") {
        setLogoUrl(await uploadDirectoryImage(files[0]!));
      } else if (target === "cover") {
        setCoverUrl(await uploadDirectoryImage(files[0]!));
      } else {
        const uploaded: string[] = [];
        for (const file of files.slice(0, 8)) uploaded.push(await uploadDirectoryImage(file));
        setGallery((previous) => [...previous, ...uploaded].slice(0, 8));
      }
    } catch (uploadError) {
      setSaveError(uploadError instanceof Error ? uploadError.message : "No se pudo subir la imagen.");
    } finally {
      event.target.value = "";
      setImageBusy(false);
    }
  }

  if (!id) {
    return <p className="mt-6 text-red-600">ID de negocio invalido.</p>;
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl">
        <div className="border-sand/40 bg-paper h-10 w-64 animate-pulse rounded-md border" />
        <div className="border-sand/40 bg-paper mt-6 h-72 animate-pulse rounded-md border" />
        <p className="sr-only">Cargando negocio...</p>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="max-w-5xl">
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700"
        >
          <p>{error instanceof Error ? error.message : "No pudimos cargar este negocio."}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  const state = describeStoreState(store);
  const effectiveEditorial = editorialStatus;
  const canPublishNow = effectiveEditorial === "visible";
  const saasUrl = store.origin === "saas" ? saasManagementUrl(store, saasAdminBaseUrl) : null;
  const controlsDisabled = !userCanEdit;
  const contentDisabled = !userCanEdit || !isManual;
  const formDisabled = !userCanEdit;

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-ink text-2xl font-bold">Editar negocio</h1>
            <Badge tone={isManual ? "signal" : "neutral"}>{isManual ? "Manual" : "SaaS"}</Badge>
            <Badge tone={state.isWebVisible ? "market" : "neutral"}>{state.webLabel}</Badge>
          </div>
          <p className="text-mist mt-1 text-sm">
            {store.name} — {store.city}
            <span aria-hidden="true"> · </span>
            {store.category}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={`${publicSiteUrl}/tienda/${encodeURIComponent(store.slug)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink border-ink/15 hover:border-ink/30 hover:bg-ink/5 focus-visible:ring-gold inline-flex h-11 items-center rounded-full border px-5 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
          >
            Ver ficha publica
            <span className="sr-only"> (se abre en una pestana nueva)</span>
          </a>
          <Link to="/directorio">
            <Button type="button" variant="ghost">
              Volver
            </Button>
          </Link>
        </div>
      </div>

      {!userCanEdit ? (
        <p
          role="status"
          className="border-sand/50 bg-sand/20 text-ink mt-5 rounded-md border px-4 py-3 text-sm"
        >
          Tu rol actual solo permite visualizar el directorio. Las modificaciones estan deshabilitadas.
        </p>
      ) : null}

      {store.origin === "saas" ? (
        <div className="border-gold/40 bg-signal/10 text-ink mt-5 rounded-md border px-4 py-4 text-sm">
          <p className="font-medium">Esta tienda se sincroniza desde el SaaS.</p>
          <p className="text-mist mt-1 leading-relaxed">
            Su nombre, ubicacion, categoria, imagenes y demas datos publicos pertenecen al SaaS y se
            sobreescribirian en la proxima sincronizacion, por lo que aqui solo se muestran. Su publicacion
            tambien la gobierna el SaaS (el dueno activa &laquo;Mostrar en directorio&raquo;). En este panel
            puedes administrar su revision editorial, destacado, orden y el bloqueo en la web.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            {store.saas_store_id ? (
              <span className="text-mist">
                ID en el SaaS: <code className="font-mono">{store.saas_store_id}</code>
              </span>
            ) : null}
            {saasUrl ? (
              <a
                href={saasUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink hover:text-gold underline underline-offset-2"
              >
                Abrir en el SaaS
                <span className="sr-only"> (se abre en una pestana nueva)</span>
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
        <Section
          title="Informacion del negocio"
          description={
            isManual
              ? "Datos publicos de la ficha en /tiendas."
              : "Datos sincronizados desde el SaaS (solo lectura)."
          }
        >
          {isManual ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Nombre comercial"
                value={form.name}
                error={fieldErrors.name}
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <CategorySelect
                label="Categoria"
                value={categoryId}
                error={fieldErrors.category}
                disabled={formDisabled}
                hint="Categoria controlada por Vimos. Elige la general o una subcategoria."
                onChange={setCategoryId}
              />
              <Input
                label="Ciudad / ubicacion principal"
                value={form.city}
                error={fieldErrors.city}
                hint="Se usa para agrupar el negocio en /tiendas."
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, city: event.target.value })}
              />
              <UbigeoSelect
                idPrefix="store-edit-ubigeo"
                className="sm:col-span-2"
                value={{ department: form.department, province: form.province, district: form.district }}
                disabled={formDisabled}
                onChange={(next) => setForm({ ...form, ...next })}
              />
              <Input
                label="Direccion"
                value={form.address}
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, address: event.target.value })}
              />
              <Input
                label="Pagina web"
                type="text"
                inputMode="url"
                value={form.website_url}
                error={fieldErrors.website_url}
                hint="Opcional. Pega el enlace con o sin https://."
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, website_url: event.target.value })}
              />
              <Input
                label="Telefono"
                value={form.phone}
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
              <Input
                label="WhatsApp"
                value={form.whatsapp}
                disabled={formDisabled}
                onChange={(event) => setForm({ ...form, whatsapp: event.target.value })}
              />
              <div className="sm:col-span-2">
                <h3 className="font-body text-ink text-sm font-medium">Redes sociales (opcionales)</h3>
                <p className="text-mist mt-1 text-xs">Usa enlaces de perfil de cada plataforma.</p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {(["tiktok", "instagram", "facebook"] as const).map((network) => {
                    const label =
                      network === "tiktok" ? "TikTok" : network === "instagram" ? "Instagram" : "Facebook";
                    const placeholder =
                      network === "tiktok"
                        ? "https://www.tiktok.com/@tunegocio"
                        : network === "instagram"
                          ? "https://www.instagram.com/tu-negocio/"
                          : "https://www.facebook.com/tu.negocio";
                    return (
                      <Input
                        key={network}
                        label={label}
                        type="text"
                        inputMode="url"
                        value={socialLinks[network]}
                        placeholder={placeholder}
                        disabled={formDisabled}
                        onChange={(event) =>
                          setSocialLinks((current) => ({ ...current, [network]: event.target.value }))
                        }
                      />
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <LocationPicker
                  value={form.location}
                  onChange={(nextLocation) => setForm({ ...form, location: nextLocation })}
                  addressHint={form.address || form.district || form.province}
                  disabled={formDisabled}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-body text-ink text-sm font-medium" htmlFor="store-description">
                  Descripcion
                </label>
                <textarea
                  id="store-description"
                  className={`${textareaClass} mt-1.5`}
                  maxLength={5000}
                  value={form.description}
                  disabled={formDisabled}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
                {fieldErrors.description ? (
                  <p role="alert" className="mt-1 text-sm text-red-600">
                    {fieldErrors.description}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <ReadOnlyField label="Nombre comercial" value={store.name} />
              <ReadOnlyField label="Categoria" value={store.category} />
              <ReadOnlyField label="Ciudad" value={store.city} />
              <ReadOnlyField
                label="Ubicacion"
                value={[store.district, store.province, store.department].filter(Boolean).join(", ")}
              />
              <ReadOnlyField label="Direccion" value={store.address ?? ""} />
              <ReadOnlyField
                label="Ubicacion en el mapa"
                value={
                  store.latitude != null && store.longitude != null
                    ? "Punto exacto confirmado"
                    : "Sin punto exacto (solo direccion)"
                }
              />
              <ReadOnlyField label="Pagina web" value={store.website_url ?? ""} />
              <ReadOnlyField label="Telefono" value={store.phone ?? ""} />
              <ReadOnlyField label="WhatsApp" value={store.whatsapp ?? ""} />
              <div className="sm:col-span-2">
                <h3 className="font-body text-ink text-sm font-medium">
                  Redes sociales gestionadas por Vimos
                </h3>
                <p className="text-mist mt-1 text-xs">
                  Estos enlaces se conservan durante la sincronización con el SaaS.
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {(["tiktok", "instagram", "facebook"] as const).map((network) => {
                    const label =
                      network === "tiktok" ? "TikTok" : network === "instagram" ? "Instagram" : "Facebook";
                    const placeholder =
                      network === "tiktok"
                        ? "https://www.tiktok.com/@tunegocio"
                        : network === "instagram"
                          ? "https://www.instagram.com/tu-negocio/"
                          : "https://www.facebook.com/tu.negocio";
                    return (
                      <Input
                        key={network}
                        label={label}
                        type="text"
                        inputMode="url"
                        value={socialLinks[network]}
                        placeholder={placeholder}
                        disabled={!userCanEdit}
                        onChange={(event) =>
                          setSocialLinks((current) => ({ ...current, [network]: event.target.value }))
                        }
                      />
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <ReadOnlyField label="Descripcion" value={store.description ?? ""} />
              </div>
            </div>
          )}
        </Section>

        <Section
          title="Clasificacion en el directorio"
          description="Categoria y palabras clave del catalogo. Las gobierna Vimos: aplican a negocios manuales y del SaaS, y la sincronizacion nunca las sobrescribe."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <CategorySelect
              label="Categoria del directorio"
              value={categoryId}
              disabled={controlsDisabled}
              hint="Un filtro general incluye sus subcategorias en /tiendas."
              onChange={setCategoryId}
            />
            <KeywordsInput
              label="Palabras clave de busqueda"
              value={keywords}
              disabled={controlsDisabled}
              hint="Opcional, hasta 3. El buscador prioriza coincidencias exactas."
              onChange={setKeywords}
            />
          </div>
        </Section>

        <Section
          title="Imagenes"
          description={
            isManual
              ? "Logo, portada y galeria que se muestran en la ficha."
              : "Imagenes sincronizadas desde el SaaS (solo lectura)."
          }
        >
          {isManual ? (
            <div className="grid gap-5 sm:grid-cols-3">
              <div>
                <label className="font-body text-ink text-sm font-medium" htmlFor="store-logo">
                  Logo
                </label>
                <input
                  id="store-logo"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="mt-1.5 block text-sm"
                  disabled={contentDisabled || imageBusy}
                  onChange={(event) => void handleImage(event, "logo")}
                />
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo actual" className="mt-2 h-16 w-16 rounded object-cover" />
                ) : null}
              </div>
              <div>
                <label className="font-body text-ink text-sm font-medium" htmlFor="store-cover">
                  Portada
                </label>
                <input
                  id="store-cover"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="mt-1.5 block text-sm"
                  disabled={contentDisabled || imageBusy}
                  onChange={(event) => void handleImage(event, "cover")}
                />
                {coverUrl ? (
                  <img src={coverUrl} alt="Portada actual" className="mt-2 h-16 w-28 rounded object-cover" />
                ) : null}
              </div>
              <div>
                <label className="font-body text-ink text-sm font-medium" htmlFor="store-gallery">
                  Galeria (max. 8)
                </label>
                <input
                  id="store-gallery"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="mt-1.5 block text-sm"
                  disabled={contentDisabled || imageBusy}
                  onChange={(event) => void handleImage(event, "gallery")}
                />
                {gallery.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {gallery.map((url) => (
                      <div key={url} className="relative">
                        <img src={url} alt="Galeria" className="h-14 w-14 rounded object-cover" />
                        <button
                          type="button"
                          aria-label={`Quitar imagen ${url}`}
                          disabled={contentDisabled}
                          className="bg-ink/80 absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white"
                          onClick={() => setGallery((current) => current.filter((item) => item !== url))}
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              {logoUrl ? <img src={logoUrl} alt="Logo" className="h-16 w-16 rounded object-cover" /> : null}
              {coverUrl ? (
                <img src={coverUrl} alt="Portada" className="h-16 w-28 rounded object-cover" />
              ) : null}
              {gallery.map((url) => (
                <img key={url} src={url} alt="Galeria" className="h-16 w-16 rounded object-cover" />
              ))}
              {!logoUrl && !coverUrl && gallery.length === 0 ? (
                <p className="text-mist text-sm">Sin imagenes registradas.</p>
              ) : null}
            </div>
          )}
        </Section>

        <Section
          title="Publicacion y revision"
          description="La revision editorial aprueba el negocio; la publicacion lo muestra en /tiendas. Son estados distintos."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="font-body text-ink text-sm font-medium" htmlFor="store-editorial">
                Revision editorial
              </label>
              <select
                id="store-editorial"
                className={selectClass}
                value={editorialStatus}
                disabled={controlsDisabled}
                onChange={(event) => setEditorialStatus(event.target.value as EditorialStatus)}
              >
                <option value="visible">Aprobada</option>
                <option value="review">En revision</option>
                <option value="hidden">Oculta editorial</option>
              </select>
            </div>

            {isManual ? (
              <div className="flex flex-col gap-1.5">
                <span className="font-body text-ink text-sm font-medium">Publicacion en /tiendas</span>
                <label className="border-sand/60 bg-paper text-ink flex items-center gap-2 rounded-md border px-3 py-3 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C9A24D]"
                    checked={isVisible}
                    disabled={controlsDisabled || !canPublishNow}
                    onChange={(event) => setIsVisible(event.target.checked)}
                  />
                  Publicado
                </label>
                {!canPublishNow ? (
                  <p className="text-mist text-xs">Para publicarlo, aprueba primero la revision editorial.</p>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="font-body text-ink text-sm font-medium">Publicacion en /tiendas</span>
                <p className="border-sand/50 bg-sand/10 text-ink rounded-md border px-3.5 py-2.5 text-sm">
                  {isVisible ? "Publicada segun el SaaS" : "Sin publicar segun el SaaS"}
                </p>
                <p className="text-mist text-xs">
                  Gestionada por el dueno en el SaaS. Usa &laquo;Ocultar en la web&raquo; si necesitas
                  bloquearla temporalmente.
                </p>
              </div>
            )}

            <label className="text-ink flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#C9A24D]"
                checked={isFeatured}
                disabled={controlsDisabled}
                onChange={(event) => setIsFeatured(event.target.checked)}
              />
              Destacado
            </label>

            <div className="flex flex-col gap-1.5">
              <label className="font-body text-ink text-sm font-medium" htmlFor="store-sort-order">
                Orden de aparicion
              </label>
              <input
                id="store-sort-order"
                type="number"
                className={selectClass}
                value={sortOrder}
                disabled={controlsDisabled}
                onChange={(event) => setSortOrder(Number(event.target.value) || 0)}
              />
            </div>

            <label className="text-ink flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#C9A24D]"
                checked={isForceHidden}
                disabled={controlsDisabled}
                onChange={(event) => setIsForceHidden(event.target.checked)}
              />
              Bloquear en la web (ocultar forzado)
            </label>
          </div>

          <p className="text-mist mt-3 text-xs">
            Estado publico actual: <span className="text-ink font-medium">{state.webLabel}</span>.{" "}
            {state.webDetail}
          </p>
        </Section>

        {saveError ? (
          <p
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {saveError}
          </p>
        ) : null}

        <div className="border-sand/40 sticky bottom-0 flex flex-wrap gap-3 border-t bg-[#f7f7f4]/95 py-4 backdrop-blur">
          <Button type="submit" isLoading={save.isPending} disabled={!userCanEdit || !dirty}>
            Guardar cambios
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel} disabled={save.isPending}>
            Cancelar
          </Button>
          {dirty ? <span className="text-mist self-center text-xs">Cambios sin guardar</span> : null}
        </div>
      </form>

      <ConfirmDialog
        open={confirmCancel}
        title="Descartar cambios"
        message="Tienes cambios sin guardar. Si sales ahora se perderan."
        confirmLabel="Salir sin guardar"
        tone="danger"
        onConfirm={() => {
          setConfirmCancel(false);
          navigate("/directorio");
        }}
        onCancel={() => setConfirmCancel(false)}
      />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
